import { computed, ComputedRef, ref } from '@vue/composition-api';

type Position = {
  x: number;
  y: number;
};

type UseImageZoomParams = {
  enableZoom: boolean;
};

type UseImageZoomReturn = {
  zoomIn: () => void;
  zoomOut: () => void;
  resetZoom: () => void;
  canZoomIn: ComputedRef<boolean>;
  canZoomOut: ComputedRef<boolean>;
  canDrag: ComputedRef<boolean>;
  startDrag: (event: MouseEvent) => void;
  onDrag: (event: MouseEvent) => void;
  endDrag: () => void;
  panZoomStyle: ComputedRef<Record<string, string>>;
};

export default function useImageZoom(params: UseImageZoomParams): UseImageZoomReturn {
  const ZOOM_CONFIG = {
    STEP: 1.2,
    MAX: 1.2 ** 10,
    MIN: 1.0,
  };
  const zoomLevel = ref<number>(1.0);
  const imagePosition = ref<Position>({ x: 0, y: 0 });
  const isDragging = ref<boolean>(false);
  const dragStart = ref<Position>({ x: 0, y: 0 });
  const canZoomIn = computed<boolean>(() => {
    return params.enableZoom && zoomLevel.value < ZOOM_CONFIG.MAX;
  });
  const canZoomOut = computed<boolean>(() => {
    return params.enableZoom && zoomLevel.value > ZOOM_CONFIG.MIN;
  });
  const canDrag = computed<boolean>(() => {
    return canZoomOut.value;
  });
  const zoomIn = () => {
    if (!canZoomIn.value) return;
    zoomLevel.value *= ZOOM_CONFIG.STEP;
  };
  const zoomOut = () => {
    if (!canZoomOut.value) return;

    zoomLevel.value /= ZOOM_CONFIG.STEP;
    if (zoomLevel.value <= ZOOM_CONFIG.MIN) {
      resetZoom();
    }
  };
  const resetZoom = () => {
    if (!params.enableZoom) return;

    zoomLevel.value = ZOOM_CONFIG.MIN;
    imagePosition.value = { x: 0, y: 0 };
  };
  const startDrag = (event: MouseEvent) => {
    isDragging.value = true;
    dragStart.value = {
      x: event.clientX - imagePosition.value.x,
      y: event.clientY - imagePosition.value.y,
    };
  };
  const onDrag = (event: MouseEvent) => {
    if (!isDragging.value) return;

    imagePosition.value = {
      x: event.clientX - dragStart.value.x,
      y: event.clientY - dragStart.value.y,
    };
  };
  const endDrag = () => {
    isDragging.value = false;
  };
  const panZoomStyle = computed<Record<string, string>>(() => {
    if (!params.enableZoom) {
      return {
        transform: 'none',
        cursor: 'default',
        transition: 'none',
      };
    }

    const translate = `translate(${imagePosition.value.x}px, ${imagePosition.value.y}px)`;
    const scale = `scale(${zoomLevel.value})`;
    const transformStyle = `${translate} ${scale}`;
    const cursorStyle = canZoomOut.value ? (isDragging.value ? 'move' : 'pointer') : 'default';
    const transitionStyle = isDragging.value ? 'none' : 'transform 0.2s ease-out';

    return {
      transform: transformStyle,
      cursor: cursorStyle,
      transition: transitionStyle,
    };
  });

  return {
    zoomIn,
    zoomOut,
    resetZoom,
    canZoomIn,
    canZoomOut,
    canDrag,
    startDrag,
    onDrag,
    endDrag,
    panZoomStyle,
  };
}
